import { env } from "@/config/env";
import type { BookListCoverageItem, BookListItem, CatalogQuery, CatalogTitle, CatalogTitleDetail, Category, ConditionEvent, Copy, DamageReport, DashboardCounts, FindResult, Handout, IsbnLookupResult, Library, ListResponse, Loan, Me, OpenLoanReportItem, OverdueReportItem, PortalCard, SchoolClass, SchoolClassDetail, SchoolYear, ScanResult, StartSchoolYearResult, Stocktake, StocktakeReport, StocktakeScanResult, StockReportItem, Student, StudentDetail, Subject, SyncOp, SyncOpResult, TeacherClassDetail, TeacherClassSummary, TeacherStudentDetail, Title, TitleDetail, User, PortalBookList, PortalLibrary, LibrarySettings, ExtendedLoan, CatalogAbout } from "@/types/api";

/**
 * One typed client per endpoint in `Bookbase/API/documentation.md` (the backend's
 * own contract). The API's errors already carry a German message
 * (`{"error": {"code", "message"}}`); ApiError just surfaces it. Network-level
 * failures (no response at all) get a fallback message from the caller via
 * `ApiError.isNetworkError`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isNetworkError() {
    return this.status === 0;
  }
}

let authTokenProvider: () => string | null = () => null;
let libraryIdProvider: () => number | null = () => null;

/** Wired once by AuthContext / LibraryContext at app start; avoids a circular import. */
export function setAuthTokenProvider(provider: () => string | null) {
  authTokenProvider = provider;
}
export function setLibraryIdProvider(provider: () => number | null) {
  libraryIdProvider = provider;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /**
   * Attach X-Library-Id. Per documentation.md's "Notes for the infrastructure and
   * frontend builders": required on POST /loans, POST /returns, POST /sync,
   * POST /stocktakes, and POST /stocktakes/{id}/scans; ignored everywhere else.
   */
  withLibrary?: boolean;
  signal?: AbortSignal;
  /** GETs are retried by default; the counter's scan opts out (it must answer fast, then falls back offline). */
  retry?: boolean;
}

function buildQuery(query?: RequestOptions["query"]): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function buildHeaders(options: Pick<RequestOptions, "body" | "withLibrary">): Record<string, string> {
  const token = authTokenProvider();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = token;
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.withLibrary) {
    const libraryId = libraryIdProvider();
    if (libraryId) headers["X-Library-Id"] = String(libraryId);
  }
  return headers;
}

/** A request that takes longer than this is given up (API Gateway itself stops at 29 s). */
export const REQUEST_TIMEOUT_MS = 30_000;
/** Waits before retrying a GET: the database pauses outside school hours and needs a moment to resume. */
export const RETRY_DELAYS_MS = [1_000, 3_000];
const RETRYABLE_STATUS = new Set([0, 502, 503, 504]);

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function attempt<T>(url: string, init: RequestInit, signal?: AbortSignal): Promise<T> {
  const timeout = new AbortController();
  const timer = setTimeout(() => timeout.abort(), REQUEST_TIMEOUT_MS);
  const onAbort = () => timeout.abort();
  signal?.addEventListener("abort", onAbort);

  let response: Response;
  try {
    response = await fetch(url, { ...init, signal: timeout.signal });
  } catch {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    throw new ApiError(0, "NETWORK_ERROR", "Der Server antwortet nicht. Bitte später erneut versuchen.");
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }

  if (response.status === 204) return undefined as T;

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    // Not JSON: typically an HTML page (a wrong API_URL, a gateway or CDN error page). Never
    // treat that as a successful empty answer -- the app would then wait forever. The real
    // status stays, so a gateway's 502/503/504 page is retried like any other.
    throw new ApiError(response.status, "INVALID_RESPONSE",
      "Der Server hat unerwartet geantwortet. Bitte später erneut versuchen.");
  }
  if (!response.ok) {
    const body = ((data as { error?: unknown })?.error ?? {}) as { code?: string; message?: string; details?: Record<string, unknown> };
    throw new ApiError(response.status, body.code ?? "INTERNAL_ERROR", body.message ?? "Es ist ein Fehler aufgetreten.", body.details ?? {});
  }
  return data as T;
}

/**
 * One API call. Every request has a time limit (`REQUEST_TIMEOUT_MS`). A GET that gets no
 * answer, or a 502/503/504 (e.g. while the paused database resumes), is tried again after
 * `RETRY_DELAYS_MS`; anything that changes data is sent once only, so it never happens twice.
 */
async function request<T>(base: "management" | "portal", path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const url = `${env.apiUrl}/${base}${path}${buildQuery(options.query)}`;
  const init: RequestInit = {
    method,
    headers: buildHeaders(options),
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  };
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  const delays = method === "GET" && options.retry !== false && !offline ? RETRY_DELAYS_MS : [];
  for (let tryNumber = 0; ; tryNumber += 1) {
    try {
      return await attempt<T>(url, init, options.signal);
    } catch (error) {
      const retryable = error instanceof ApiError && RETRYABLE_STATUS.has(error.status);
      if (!retryable || tryNumber >= delays.length || options.signal?.aborted) throw error;
      await wait(delays[tryNumber]);
    }
  }
}

/** Report endpoints with `?format=csv` respond `text/csv` with a Content-Disposition header. */
async function downloadCsv(base: "management" | "portal", path: string, query: RequestOptions["query"] = {}, withLibrary = false): Promise<Blob> {
  const headers = buildHeaders({ withLibrary });
  const response = await fetch(`${env.apiUrl}/${base}${path}${buildQuery({ ...query, format: "csv" })}`, { headers });
  if (!response.ok) throw new ApiError(response.status, "DOWNLOAD_FAILED", "Der Bericht konnte nicht geladen werden.");
  return response.blob();
}

const management = {
  me: () => request<Me>("management", "/me"),
  dashboard: () => request<DashboardCounts>("management", "/dashboard"),
  libraries: () => request<ListResponse<Library>>("management", "/libraries"),
  updateLibrary: (id: number, body: Partial<LibrarySettings> & { student_searchable?: boolean }) =>
    request<Library>("management", `/libraries/${id}`, { method: "PUT", body }),
  setLibraryStaff: (id: number, userIds: number[]) =>
    request<{ id: number; staff_user_ids: number[] }>("management", `/libraries/${id}/staff`, { method: "PUT", body: { user_ids: userIds } }),
  extendLoan: (id: number) => request<ExtendedLoan>("management", `/loans/${id}/extend`, { method: "POST" }),
  scan: (code: string) => request<ScanResult>("management", `/scan/${encodeURIComponent(code)}`, { retry: false }),
  isbnLookup: (isbn: string) => request<IsbnLookupResult>("management", `/isbn/${encodeURIComponent(isbn)}`),

  titles: (params: { q?: string; subject_id?: number; category_id?: number; library_id?: number; limit?: number; offset?: number }) =>
    request<ListResponse<Title>>("management", "/titles", { query: params }),
  createTitle: (body: {
    title: string;
    isbn?: string;
    subtitle?: string | null;
    description?: string | null;
    authors?: string | null;
    publisher?: string | null;
    published_date?: string | null;
    language?: string | null;
    page_count?: number | null;
    subject_id?: number | null;
    category_ids?: number[];
    cover_temp_key?: string | null;
  }) => request<{ id: number; isbn13: string | null; title: string; cover_key: string | null }>("management", "/titles", { method: "POST", body }),
  title: (id: number) => request<TitleDetail>("management", `/titles/${id}`),
  updateTitle: (id: number, body: Partial<Omit<Title, "cover_key">> & { isbn?: string; category_ids?: number[]; cover_temp_key?: string | null }) =>
    request<TitleDetail>("management", `/titles/${id}`, { method: "PUT", body }),
  deleteTitle: (id: number) => request<{ deleted: true }>("management", `/titles/${id}`, { method: "DELETE" }),
  /** Presigned PUT, 5 min. Caller PUTs the file bytes directly to `upload_url` with the same content_type. */
  coverUploadUrl: (id: number, contentType: string) =>
    request<{ upload_url: string; cover_temp_key: string }>("management", `/titles/${id}/cover-upload`, { method: "POST", body: { content_type: contentType } }),
  addCopies: (id: number, body: { library_id: number; count: number; acquired_on?: string; location?: string; note?: string }) =>
    request<{ copy_ids: number[] }>("management", `/titles/${id}/copies`, { method: "POST", body }),

  copy: (code: string) => request<Copy>("management", `/copies/${encodeURIComponent(code)}`),
  updateCopy: (code: string, body: { location?: string; note?: string; condition?: "GOOD" | "DAMAGED" }) =>
    request<Copy>("management", `/copies/${encodeURIComponent(code)}`, { method: "PATCH", body }),
  withdrawCopy: (code: string, body: { reason: "LOST" | "OLD" | "DAMAGED" | "OTHER"; note?: string }) =>
    request<Copy>("management", `/copies/${encodeURIComponent(code)}/withdraw`, { method: "POST", body }),
  deleteCopy: (code: string) => request<{ deleted: true }>("management", `/copies/${encodeURIComponent(code)}`, { method: "DELETE" }),
  copyHistory: (code: string) =>
    request<{ loans: Loan[]; condition_events: ConditionEvent[] }>("management", `/copies/${encodeURIComponent(code)}/history`),

  labels: (body: { codes: string[] } | { title_id: number; library_id: number }) =>
    request<{ items: { code: string; title: string; library_name: string }[] }>("management", "/labels", { method: "POST", body }),

  students: (params: { q?: string; class_id?: number; category_id?: number; active?: boolean; needs_class?: boolean }) =>
    request<ListResponse<Student>>("management", "/students", { query: params }),
  createStudent: (body: { given_name: string; family_name: string; government_id?: string; class_id?: number; category_id?: number; with_login?: boolean }) =>
    request<Student & { account?: { username: string; initial_password: string } }>("management", "/students", { method: "POST", body }),
  student: (id: number) => request<StudentDetail>("management", `/students/${id}`),
  handout: (id: number) => request<Handout>("management", `/students/${id}/handout`, { withLibrary: true }),
  updateStudent: (id: number, body: { given_name?: string; family_name?: string; class_id?: number | null; category_id?: number | null; government_id?: string | null }) =>
    request<StudentDetail>("management", `/students/${id}`, { method: "PUT", body }),
  activateStudent: (id: number) => request<Student>("management", `/students/${id}/activate`, { method: "POST" }),
  deactivateStudent: (id: number) => request<Student>("management", `/students/${id}/deactivate`, { method: "POST" }),
  createStudentAccount: (id: number) => request<{ username: string; initial_password: string }>("management", `/students/${id}/account`, { method: "POST" }),
  bulkDeactivateStudents: (body: { class_id?: number; student_ids?: number[] }) =>
    request<{ deactivated: number }>("management", "/students/bulk-deactivate", { method: "POST", body }),

  lend: (body: { student_id: number; copy_code: string; due_on?: string }) =>
    request<{ id: number; copy_id: number; student_id: number; due_on: string | null; title?: string }>("management", "/loans", { method: "POST", body, withLibrary: true }),
  returnCopy: (body: { copy_code: string; condition: "GOOD" | "DAMAGED" | "UNUSABLE"; note?: string; expected_student_id?: number }) =>
    request<{ loan_id: number; copy_id: number; condition: string; copy_status: string; title?: string }>("management", "/returns", { method: "POST", body, withLibrary: true }),
  loans: (params: { open?: boolean; student_id?: number; class_id?: number; overdue?: boolean; library_id?: number }) =>
    request<ListResponse<Loan>>("management", "/loans", { query: params }),
  sync: (ops: SyncOp[]) => request<{ results: SyncOpResult[] }>("management", "/sync", { method: "POST", body: { ops }, withLibrary: true }),

  damageReports: (params: { status?: "OPEN" | "RESOLVED"; library_id?: number }) =>
    request<ListResponse<DamageReport>>("management", "/damage-reports", { query: params }),
  createDamageReport: (body: { copy_id: number; kind: "LOST" | "DAMAGED"; reason: string; loan_id?: number; student_id?: number }) =>
    request<{ id: number }>("management", "/damage-reports", { method: "POST", body }),
  resolveDamageReport: (id: number, body: { resolution: string; note?: string }) =>
    request<{ id: number; resolution: string }>("management", `/damage-reports/${id}/resolve`, { method: "POST", body }),

  classes: (params: { active?: boolean } = {}) => request<ListResponse<SchoolClass>>("management", "/classes", { query: params }),
  createClass: (body: { name: string; entry_year: number; entry_grade: number; last_grade: number; sector: "SESB" | "REGEL" }) =>
    request<SchoolClass>("management", "/classes", { method: "POST", body }),
  classDetail: (id: number) => request<SchoolClassDetail>("management", `/classes/${id}`),
  updateClass: (id: number, body: { name?: string; last_grade?: number; sector?: "SESB" | "REGEL" }) =>
    request<SchoolClass>("management", `/classes/${id}`, { method: "PUT", body }),
  deleteClass: (id: number) => request<{ deleted: true }>("management", `/classes/${id}`, { method: "DELETE" }),
  assignStudentsToClass: (id: number, studentIds: number[]) =>
    request<SchoolClassDetail>("management", `/classes/${id}/students`, { method: "POST", body: { student_ids: studentIds } }),
  setClassTeachers: (id: number, userIds: number[]) =>
    request<SchoolClassDetail>("management", `/classes/${id}/teachers`, { method: "PUT", body: { user_ids: userIds } }),

  schoolYears: () => request<ListResponse<SchoolYear>>("management", "/school-years"),
  startSchoolYear: () => request<StartSchoolYearResult>("management", "/school-years", { method: "POST" }),

  categories: () => request<ListResponse<Category>>("management", "/categories"),
  createCategory: (name: string) => request<Category>("management", "/categories", { method: "POST", body: { name } }),
  updateCategory: (id: number, name: string) => request<Category>("management", `/categories/${id}`, { method: "PUT", body: { name } }),
  deleteCategory: (id: number) => request<{ deleted: true }>("management", `/categories/${id}`, { method: "DELETE" }),

  subjects: () => request<ListResponse<Subject>>("management", "/subjects"),
  createSubject: (name: string) => request<Subject>("management", "/subjects", { method: "POST", body: { name } }),
  updateSubject: (id: number, name: string) => request<Subject>("management", `/subjects/${id}`, { method: "PUT", body: { name } }),
  deleteSubject: (id: number) => request<{ deleted: true }>("management", `/subjects/${id}`, { method: "DELETE" }),

  bookLists: (params: { school_year_id?: number; grade?: number }) => request<ListResponse<BookListItem>>("management", "/book-lists", { query: params }),
  createBookListItem: (body: { school_year_id: number; grade: number; title_id: number; applies_sesb?: boolean; applies_regel?: boolean; note?: string }) =>
    request<{ id: number }>("management", "/book-lists", { method: "POST", body }),
  updateBookListItem: (id: number, body: { applies_sesb?: boolean; applies_regel?: boolean; note?: string }) =>
    request<{ id: number }>("management", `/book-lists/${id}`, { method: "PUT", body }),
  deleteBookListItem: (id: number) => request<{ deleted: true }>("management", `/book-lists/${id}`, { method: "DELETE" }),
  copyBookList: (fromSchoolYearId: number, toSchoolYearId: number) =>
    request<{ copied: number }>("management", "/book-lists/copy", { method: "POST", body: { from_school_year_id: fromSchoolYearId, to_school_year_id: toSchoolYearId } }),

  stocktakes: (params: { library_id?: number } = {}) => request<ListResponse<Stocktake>>("management", "/stocktakes", { query: params }),
  startStocktake: () => request<Stocktake>("management", "/stocktakes", { method: "POST", withLibrary: true }),
  stocktake: (id: number) => request<Stocktake>("management", `/stocktakes/${id}`),
  scanStocktake: (id: number, code: string) =>
    request<StocktakeScanResult>("management", `/stocktakes/${id}/scans`, { method: "POST", body: { code }, withLibrary: true }),
  closeStocktake: (id: number) => request<Stocktake>("management", `/stocktakes/${id}/close`, { method: "POST" }),
  stocktakeReport: (id: number) => request<StocktakeReport>("management", `/stocktakes/${id}/report`),

  users: (params: { role?: string; q?: string; active?: boolean }) => request<ListResponse<User>>("management", "/users", { query: params }),
  createUser: (body: { role: string; given_name: string; family_name: string; email?: string; username?: string }) =>
    request<{ id: number; username: string; role: string; initial_password: string }>("management", "/users", { method: "POST", body }),
  updateUser: (id: number, body: { given_name?: string; family_name?: string; email?: string }) => request<User>("management", `/users/${id}`, { method: "PUT", body }),
  activateUser: (id: number) => request<{ id: number; is_active: true }>("management", `/users/${id}/activate`, { method: "POST" }),
  deactivateUser: (id: number) => request<{ id: number; is_active: false }>("management", `/users/${id}/deactivate`, { method: "POST" }),
  resetUserPassword: (id: number) => request<{ id: number; initial_password: string }>("management", `/users/${id}/reset-password`, { method: "POST" }),

  reportOpenLoans: (params: { class_id?: number; library_id?: number }) =>
    request<ListResponse<OpenLoanReportItem>>("management", "/reports/open-loans", { query: params }),
  reportOpenLoansCsv: (params: { class_id?: number; library_id?: number }) => downloadCsv("management", "/reports/open-loans", params),
  reportOverdue: () => request<ListResponse<OverdueReportItem>>("management", "/reports/overdue"),
  reportOverdueCsv: () => downloadCsv("management", "/reports/overdue"),
  reportStock: () => request<ListResponse<StockReportItem>>("management", "/reports/stock"),
  reportStockCsv: () => downloadCsv("management", "/reports/stock"),
  reportBookListCoverage: (classId: number) => request<ListResponse<BookListCoverageItem>>("management", "/reports/book-list-coverage", { query: { class_id: classId } }),
  reportBookListCoverageCsv: (classId: number) => downloadCsv("management", "/reports/book-list-coverage", { class_id: classId }),
};

const portal = {
  me: () => request<Me>("portal", "/me"),
  card: () => request<PortalCard>("portal", "/card"),
  loans: () => request<ListResponse<Loan>>("portal", "/loans"),
  extendLoan: (id: number) => request<ExtendedLoan>("portal", `/loans/${id}/extend`, { method: "POST" }),
  reportLoan: (id: number, body: { kind: "LOST" | "DAMAGED"; reason: string }) =>
    request<{ id: number }>("portal", `/loans/${id}/report`, { method: "POST", body }),
  bookList: () => request<PortalBookList>("portal", "/book-list"),
  libraries: () => request<ListResponse<PortalLibrary>>("portal", "/libraries"),
  catalog: (params: CatalogQuery) => request<ListResponse<CatalogTitle>>("portal", "/catalog", { query: params }),
  catalogTitle: (id: number) => request<CatalogTitleDetail>("portal", `/catalog/${id}`),
  catalogAbout: (id: number) => request<CatalogAbout>("portal", `/catalog/${id}/about`),
  find: (code: string) => request<FindResult>("portal", `/find/${encodeURIComponent(code)}`),

  teacherClasses: () => request<ListResponse<TeacherClassSummary>>("portal", "/teacher/classes"),
  teacherClass: (id: number) => request<TeacherClassDetail>("portal", `/teacher/classes/${id}`),
  teacherClassStudent: (classId: number, studentId: number) =>
    request<TeacherStudentDetail>("portal", `/teacher/classes/${classId}/students/${studentId}`),
  teacherBookLists: (params: { grade?: number; school_year_id?: number }) => request<ListResponse<BookListItem>>("portal", "/teacher/book-lists", { query: params }),
  teacherClassReport: (classId: number) => request<ListResponse<OpenLoanReportItem>>("portal", `/teacher/classes/${classId}/report`),
  teacherClassReportCsv: (classId: number) => downloadCsv("portal", `/teacher/classes/${classId}/report`),
  // Onboarding's email/phone confirmation goes straight to Cognito (services/cognito.ts).
};

export const apiService = { management, portal };
